import React, { useState, useContext } from 'react';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Typography,
  Grid,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import Constants from './Constants';
import { LoginInfo } from '../../App';

const CoinPurchaseDialog = ({ open, onClose, pack, onPurchase, userId }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography>{pack.coin_amount.toLocaleString()} コイン</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box py={2}>
          <Typography variant="h6" gutterBottom align="center">
            {pack.coin_amount.toLocaleString()} コイン
          </Typography>
          <Typography variant="h5" align="center" color="primary" gutterBottom>
            ¥{pack.price_jpy.toLocaleString()}
          </Typography>
          <Box mt={2}>
            <PayPalButtons
              style={{ layout: "horizontal" }}
              createOrder={(data, actions) => {
                return actions.order.create({
                  purchase_units: [{
                    amount: {
                      value: pack.price_jpy,
                      currency_code: "JPY"
                    },
                    description: `${pack.coin_amount.toLocaleString()} coins`,
                    custom_id: userId, // ユーザーIDをcustom_idに設定
                    invoice_id: `${userId}_${pack.id}_${Date.now()}` // ユニークな請求ID
                  }]
                });
              }}
              onApprove={async (data, actions) => {
                const details = await actions.order.capture();
                if (details.status === 'COMPLETED') {
                  onPurchase(pack, data.orderID, details.id, userId);
                  onClose();
                }
              }}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const CoinChip = ({ pack, onPurchase, userId }) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <Chip
        icon={Constants.CURRENCY_UNIT_LEFT_COIN}
        label={`${pack.coin_amount.toLocaleString()}コイン`}
        onClick={() => setDialogOpen(true)}
        color="primary"
        variant="outlined"
        sx={{ 
          width: '100%',
          justifyContent: 'flex-start',
          p: 1,
          height: 'auto',
          '& .MuiChip-label': {
            overflow: 'visible',
            whiteSpace: 'normal',
          },
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.04)'
          }
        }}
      />
      <CoinPurchaseDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        pack={pack}
        onPurchase={onPurchase}
        userId={userId}
      />
    </>
  );
};

// メインのコイン購入モーダル
const CoinPurchaseModal = ({ open, onClose }) => {
    // ユーザーコンテキストからユーザーIDを取得
    const { loginInfo, setLoginInfo } = useContext(LoginInfo)
    const userId = loginInfo?.userId;
  
    const [packages] = useState([
        { id: "1k", coin_amount: 1000, price_jpy: 1250 },
        { id: "2k", coin_amount: 2000, price_jpy: 2500 },
        { id: "3k", coin_amount: 3000, price_jpy: 3750 },
        { id: "4k", coin_amount: 4000, price_jpy: 5000 },
        { id: "5k", coin_amount: 5000, price_jpy: 6250 },
        { id: "10k", coin_amount: 10000, price_jpy: 12500 },
        { id: "15k", coin_amount: 15000, price_jpy: 18750 },
        { id: "20k", coin_amount: 20000, price_jpy: 25000 },
        { id: "25k", coin_amount: 25000, price_jpy: 31250 },
        { id: "30k", coin_amount: 30000, price_jpy: 37500 },
        { id: "35k", coin_amount: 35000, price_jpy: 43750 },
        { id: "40k", coin_amount: 40000, price_jpy: 50000 },
        { id: "45k", coin_amount: 45000, price_jpy: 56250 },
        { id: "50k", coin_amount: 50000, price_jpy: 62500 },
        { id: "55k", coin_amount: 55000, price_jpy: 68750 },
        { id: "60k", coin_amount: 60000, price_jpy: 75000 },
        { id: "65k", coin_amount: 65000, price_jpy: 81250 },
        { id: "70k", coin_amount: 70000, price_jpy: 87500 },
        { id: "75k", coin_amount: 75000, price_jpy: 93750 },
        { id: "80k", coin_amount: 80000, price_jpy: 100000 },
        { id: "85k", coin_amount: 85000, price_jpy: 106250 },
        { id: "90k", coin_amount: 90000, price_jpy: 112500 },
        { id: "95k", coin_amount: 95000, price_jpy: 118750 },
        { id: "100k", coin_amount: 100000, price_jpy: 125000 }
      ]);
  
    const handlePurchase = async (pack, orderId, transactionId, userId) => {
      try {
        const response = await fetch('/api/purchase-coins', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: userId,
            packageId: pack.id,
            orderId,
            paypalTransactionId: transactionId
          }),
        });
  
        if (!response.ok) {
          throw new Error('Purchase failed');
        }
  
        alert('コインの購入が完了しました！');
        onClose();
        window.location.reload();
      } catch (error) {
        console.error('Error processing purchase:', error);
        alert('購入処理中にエラーが発生しました。');
      }
    };
  
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">コインを購入</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <PayPalScriptProvider options={{ 
            "client-id": "AXp-3vfxN1BAab_2st-4bOWaAf8ERTgW_Nli6nGBBVH1QwWKyJu1xyxeuYvaKMuYzQvoEroaX7zIYCZW", 
            currency: "JPY" 
          }}>
            <Box py={2} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
              {!userId ? (
                <Typography color="error" align="center" py={3}>
                  ログインしてください。購入するにはアカウントが必要です。
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {packages.map(pack => (
                    <Grid item xs={6} sm={4} md={3} key={pack.id}>
                      <CoinChip
                        pack={pack}
                        onPurchase={handlePurchase}
                        userId={userId}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </PayPalScriptProvider>
        </DialogContent>
      </Dialog>
    );
  };

// 使用例
const CreditCardPurchaseButton = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<CreditCardIcon />}
        onClick={() => setOpen(true)}
      >
        クレジットカードでコインを購入する
      </Button>
      
      <CoinPurchaseModal
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

export default CreditCardPurchaseButton;